"use client";

/**
 * @fileoverview Lingui i18n provider component for client-side rendering.
 *
 * This component wraps the application with Lingui's I18nProvider, initializing
 * the i18n instance with the server-resolved locale and message catalog.
 *
 * When the server resolves a new locale (e.g. after the user switches language
 * and the client calls router.refresh()), the updated `initialLocale` /
 * `initialMessages` props arrive here. We keep a SINGLE stable i18n instance
 * (created once at mount) and sync locale changes via `i18n.loadAndActivate()`.
 * That call fires Lingui's "change" event, which I18nProvider's built-in
 * listener picks up and propagates to all useLingui() consumers — making the
 * locale switch take effect in the same commit cycle rather than requiring a
 * full page reload.
 *
 * @module components/lingui-provider
 */

import { i18n as globalI18n, type I18n, type Messages } from "@lingui/core";
import { setupI18n } from "@lingui/core";
import { I18nProvider } from "@lingui/react";
import { type ReactNode, useEffect, useRef } from "react";
import type { Locale } from "@/lib/i18n/locales";

/**
 * Props for the LinguiProvider component.
 */
type LinguiProviderProps = {
  /** The initial locale resolved from server (cookie or Accept-Language) */
  initialLocale: Locale;
  /** Pre-loaded message catalog for the initial locale */
  initialMessages: Messages;
  /** Child components to wrap with i18n context */
  children: ReactNode;
};

function syncGlobalI18n(locale: Locale, messages: Messages): void {
  globalI18n.load(locale, messages);
  globalI18n.activate(locale);
}

export function LinguiProvider({ initialLocale, initialMessages, children }: LinguiProviderProps) {
  // Lazy-init the i18n instance once at mount so the reference stays stable
  // across router.refresh() calls. Locale/catalog updates go through the
  // sync effect below rather than creating a new instance.
  const i18nRef = useRef<I18n | null>(null);
  if (!i18nRef.current) {
    i18nRef.current = setupI18n({
      locale: initialLocale,
      messages: { [initialLocale]: initialMessages },
    });
  }
  syncGlobalI18n(initialLocale, initialMessages);

  // Sync locale/catalog whenever the server delivers new props (e.g. after
  // router.refresh() following a locale cookie change).
  // loadAndActivate() fires a "change" event that I18nProvider's subscription
  // picks up immediately, propagating the new locale to every useLingui() consumer
  // without requiring a full page reload.
  useEffect(() => {
    const i18n = i18nRef.current!;
    if (i18n.locale !== initialLocale) {
      i18n.loadAndActivate({ locale: initialLocale, messages: initialMessages });
    }
    syncGlobalI18n(initialLocale, initialMessages);
  }, [initialLocale, initialMessages]);

  return <I18nProvider i18n={i18nRef.current as any}>{children}</I18nProvider>;
}
